'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { getPreviewViewport } from '@/lib/assets/preview-viewport'
import { ASSETS_GRID_SCROLLER_ID } from '@/lib/config'

export function useGridPageScroll(enabled: boolean, reduceMotion: boolean) {
  const pendingScroll = useRef<AbortController | null>(null)
  useEffect(() => () => pendingScroll.current?.abort(), [enabled])
  const [bounds, setBounds] = useState({ previous: false, next: false })

  useEffect(() => {
    if (!enabled) return
    const scroller = document.getElementById(ASSETS_GRID_SCROLLER_ID)
    if (!scroller) return

    const updateBounds = () => {
      const previous = scroller.scrollTop > 1
      const next = scroller.scrollTop + scroller.clientHeight < scroller.scrollHeight - 1
      setBounds((current) => (current.previous === previous && current.next === next ? current : { previous, next }))
    }

    const observer = new ResizeObserver(updateBounds)
    observer.observe(scroller)
    const content = scroller.querySelector('.root-layout-content')
    if (content) observer.observe(content)
    scroller.addEventListener('scroll', updateBounds, { passive: true })
    updateBounds()
    return () => {
      observer.disconnect()
      scroller.removeEventListener('scroll', updateBounds)
    }
  }, [enabled])

  const scrollPage = useCallback(
    (direction: -1 | 1) => {
      const scroller = document.getElementById(ASSETS_GRID_SCROLLER_ID)
      pendingScroll.current?.abort()
      if (!scroller) return Promise.resolve(null)
      const controller = new AbortController()
      pendingScroll.current = controller
      const viewport = getPreviewViewport(scroller)
      const pageHeight = Math.max(160, viewport.bottom - viewport.top)
      const origin = scroller.scrollTop
      return new Promise<HTMLElement | null>((resolve) => {
        let frame = 0
        let settled = 0
        let previous = origin
        const started = performance.now()
        const finish = () => {
          cancelAnimationFrame(frame)
          scroller.removeEventListener('scrollend', finish)
          controller.signal.removeEventListener('abort', finish)
          resolve(!controller.signal.aborted && Math.abs(scroller.scrollTop - origin) > 1 ? scroller : null)
        }
        // Older browsers lack scrollend; detect stable frames after movement instead.
        const check = () => {
          const top = scroller.scrollTop
          settled = Math.abs(top - previous) < 0.5 ? settled + 1 : 0
          previous = top
          if ((settled >= 5 && performance.now() - started > 100) || performance.now() - started > 1800) finish()
          else frame = requestAnimationFrame(check)
        }
        controller.signal.addEventListener('abort', finish, { once: true })
        scroller.addEventListener('scrollend', finish, { once: true })
        scroller.scrollBy({ top: direction * pageHeight, behavior: reduceMotion ? 'instant' : 'smooth' })
        frame = requestAnimationFrame(check)
      })
    },
    [reduceMotion]
  )

  return { ...bounds, scrollPage }
}
